import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PublicNav from "../components/PublicNav";
import SeoExpandable from "../components/SeoExpandable";

const pages = {
  privacy: {
    title: "Privacy Policy",
    subtitle: "How Play Online Satta Matka collects, uses and protects user information.",
    updated: "11 May 2026",
    sections: [
      ["Information We Collect", "We may collect your name, mobile number, email address, device information, login activity, referral information, payment request details, screenshots submitted by you, and usage activity required to run your account."],
      ["How We Use Information", "We use information to create and secure accounts, process deposits and withdrawals, provide support, send OTP and account notifications, prevent fraud, maintain platform safety, and improve our services."],
      ["Payments", "Payment screenshots, UPI references, wallet entries and withdrawal details are used only for verification, accounting, dispute handling and compliance record keeping."],
      ["Notifications", "If enabled, we may send service notifications such as account alerts, result updates, payment status updates and support messages. You can manage notification permissions from your device settings."],
      ["Data Sharing", "We do not sell personal data. We may share limited data with service providers such as hosting, analytics, OTP, Firebase notification, payment verification and support tools where needed to operate the platform."],
      ["Data Security", "We use reasonable technical and administrative safeguards to protect user information. No online service can guarantee absolute security, so users should keep account access private."],
      ["User Choices", "You may request correction of account details, support help, or account-related assistance by contacting us through the Contact Us page."],
      ["Children", "Our platform is intended only for users aged 18 years or above. We do not knowingly allow minors to register or use paid features."],
      ["Contact", "For privacy questions, contact us at support@playonlinesattamatka.com or through the WhatsApp support link on the website."]
    ]
  },
  terms: {
    title: "Terms & Conditions",
    subtitle: "Rules for using Play Online Satta Matka website and services.",
    updated: "11 May 2026",
    sections: [
      ["Acceptance", "By accessing or using Play Online Satta Matka, you agree to these Terms & Conditions and all rules shown inside the platform."],
      ["Eligibility", "You must be 18 years or older and legally allowed to use this type of service in your location. If this service is restricted in your area, you must not use it."],
      ["Account Responsibility", "Users are responsible for keeping their mobile number, OTP, password, device and account access secure. Activity from your account may be treated as your responsibility."],
      ["Game Rules", "Bets, timings, rates, results, limits and withdrawal rules shown on the website apply to all users. Bids placed after market closing may be rejected or invalid."],
      ["Wallet & Transactions", "Deposits and withdrawals are subject to verification, minimum limits, daily limits, bank/UPI correctness and admin approval where applicable."],
      ["Fair Use", "Fraud, multiple account abuse, fake payment proof, referral abuse, technical manipulation or misuse of the platform may lead to account blocking, wallet hold or cancellation of requests."],
      ["Service Availability", "We try to keep the service available, but maintenance, network issues, device problems, payment delays or third-party failures may affect access."],
      ["Changes", "We may update these terms, game rules, limits, rates or features from time to time. Continued use means you accept the updated terms."],
      ["Contact", "For support or disputes, contact support@playonlinesattamatka.com or use the support option on the website."]
    ]
  },
  refund: {
    title: "Refund & Cancellation Policy",
    subtitle: "Deposit, withdrawal and cancellation rules.",
    updated: "11 May 2026",
    sections: [
      ["Deposits", "Deposit requests are credited after payment verification. Users must submit correct payment proof and amount details."],
      ["Failed or Duplicate Payments", "If a payment is deducted but not credited, users should contact support with UPI transaction ID, date, amount and screenshot. Verified duplicate or failed credits may be adjusted to the wallet or refunded where applicable."],
      ["Bets", "Once a valid bid is placed before market closing, it generally cannot be cancelled by the user. Invalid, duplicate, technical-error or admin-reverted bets may be corrected according to platform records."],
      ["Withdrawals", "Withdrawal requests are processed according to user limits, available balance, bank/UPI details and verification checks. Incorrect details may delay or reject a request."],
      ["Refund Time", "Approved refunds or corrections may take time depending on bank, UPI or payment provider processing."],
      ["Contact", "For payment help, contact support@playonlinesattamatka.com with your registered mobile number and transaction details."]
    ]
  },
  responsible: {
    title: "Responsible Gaming",
    subtitle: "Play carefully and within your limits.",
    updated: "11 May 2026",
    sections: [
      ["18+ Only", "Play Online Satta Matka is strictly intended for users aged 18 years or above. Underage use is prohibited."],
      ["Play Responsibly", "Users should play only with money they can afford to lose. Do not borrow money or use emergency funds for gaming."],
      ["Set Limits", "Use betting, ank and withdrawal limits responsibly. If gaming causes stress, financial issues or loss of control, stop using the platform and seek help."],
      ["Self Control", "Take breaks, avoid chasing losses, and do not play while emotionally distressed or under the influence of alcohol or substances."],
      ["Restricted Locations", "Users are responsible for ensuring that use of the platform is allowed in their location."],
      ["Support", "If you need account restriction or support, contact us at support@playonlinesattamatka.com."]
    ]
  },
  contact: {
    title: "Contact Us",
    subtitle: "Support details for users, reviewers and partners.",
    updated: "11 May 2026",
    sections: [
      ["Support Email", "support@playonlinesattamatka.com"],
      ["Website", "https://playonlinesattamatka.com"],
      ["Support Hours", "Support is available for account, wallet, payment, OTP and technical issues. Response time may vary depending on request volume."],
      ["Payment Issues", "For deposit or withdrawal issues, include registered mobile number, amount, date, UPI/bank reference and screenshot if available."],
      ["Business Name", "Play Online Satta Matka"]
    ]
  },
  about: {
    title: "About Play Online Satta Matka Games",
    subtitle: "Your trusted destination for online matka play, live results, kalyan matka charts, and secure gaming.",
    updated: "11 May 2026",
    sections: [
      ["About Play Online Satta Matka", "Play Online Satta Matka is a mobile-first gaming platform built for users who want fast access to live market information, wallet tools, charts, support and game services in one clean experience. We are one of India's most trusted destinations for online matka play, offering a secure, transparent and user-friendly environment for players across the country."],
      ["Our Platform", "We provide OTP-based secure login, wallet balance tracking, market rates, real-time game result updates, passbook records, app support and admin-managed services designed for reliable daily play. Our platform supports all major markets — Kalyan, Milan Day, Milan Night, Rajdhani Day, Rajdhani Night, Time Bazar, Main Bazar and Starline — with accurate live matka results updated instantly after every declaration."],
      ["Live Results & Kalyan Matka Charts", "Play Online Satta Matka delivers live matka results and comprehensive chart data including kalyan matka charts, milan charts, rajdhani charts, panel charts and jodi charts. Charts are updated automatically after every result, giving players access to detailed historical data for analysis and reference."],
      ["User Experience", "Our focus is simple: a faster interface, clearer game flow, easier account actions and better visibility across phone, tablet and desktop devices. With all major game types supported — Single Digit, Jodi, Single Patti, Double Patti, Triple Patti, Half Sangam and Full Sangam — players have a complete online satta matka play experience in one app."],
      ["Secure Gaming", "We use industry-standard security practices to protect user accounts and transactions. Our OTP-based login system ensures that only you can access your account. All wallet transactions are logged and transparent. We never share personal data with third parties without consent."],
      ["Trust & Support", "We work to keep platform rules, transaction updates and support access clear for users. Our support team is available to help with deposits, withdrawals, OTP issues and account queries. For help, contact support@playonlinesattamatka.com."],
      ["Responsible Gaming", "Play Online Satta Matka is strictly for players aged 18 years and above. We encourage all players to set personal limits, play responsibly, and treat gaming as entertainment — not as a source of income. If you need account restrictions or support, please contact us."]
    ]
  }
};

function LegalPage({ type = "privacy" }) {
  const navigate = useNavigate();
  const page = pages[type] || pages.privacy;

  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector("meta[name='description']");
    const previousDescription = description?.getAttribute("content") || "";

    if (type === "about") {
      document.title = "About Us - Play Online Best Satta Matka Games";
      description?.setAttribute("content", "Learn more about play online satta matka, your trusted destination for online matka play, live results, kalyan matka charts, and secure gaming.");
    } else {
      document.title = `${page.title} - Play Online Satta Matka`;
      description?.setAttribute("content", `${page.title} for Play Online Satta Matka online matka play and account services.`);
    }

    return () => {
      document.title = previousTitle;
      if (description) description.setAttribute("content", previousDescription);
    };
  }, [type, page.title]);

  return (
    <main style={shell}>
      <PublicNav />

      <section style={hero}>
        <p style={eyebrow}>{type === "about" ? "Play Online Satta Matka Platform" : "Play Online Satta Matka Legal"}</p>
        <h1 style={title}>{page.title}</h1>
        <p style={subtitle}>{page.subtitle}</p>
        <span style={updated}>Last updated: {page.updated}</span>
      </section>

      <section style={card}>
        {page.sections.map(([heading, body]) => (
          <article key={heading} style={section}>
            <h2 style={sectionTitle}>{heading}</h2>
            <p style={sectionText}>{body}</p>
          </article>
        ))}
      </section>

      {type === "about" && (
        <SeoExpandable title="About Us" sections={ABOUT_SEO_SECTIONS} faqs={ABOUT_SEO_FAQS} />
      )}
    </main>
  );
}

const shell = { minHeight: "100vh", background: "linear-gradient(180deg,#f8fafc,#eef2ff)", color: "#0f172a", padding: "14px 14px 34px", boxSizing: "border-box" };
const topbar = { maxWidth: "920px", margin: "0 auto", minHeight: "56px", display: "flex", alignItems: "center", gap: "12px" };
const backButton = { width: "40px", height: "40px", borderRadius: "14px", border: "1px solid #dbe4f0", background: "#fff", color: "#334155", display: "grid", placeItems: "center", cursor: "pointer" };
const brandText = { fontWeight: 900, fontSize: "13px", letterSpacing: "0.01em" };
const brandOrange = { color: "#f97316" };
const brandGreen  = { color: "#16a34a" };
const hero = { maxWidth: "920px", margin: "14px auto 0", background: "linear-gradient(135deg,#1d4ed8,#2563eb)", color: "#fff", borderRadius: "28px", padding: "28px 22px", boxShadow: "0 18px 42px rgba(37,99,235,.20)" };
const eyebrow = { margin: 0, opacity: 0.84, fontSize: "12px", fontWeight: 900, textTransform: "uppercase", letterSpacing: ".12em" };
const title = { margin: "8px 0 0", fontSize: "clamp(28px,5vw,44px)", fontWeight: 900, lineHeight: 1.05 };
const subtitle = { margin: "10px 0 0", maxWidth: "680px", color: "#dbeafe", fontSize: "15px", lineHeight: 1.6 };
const updated = { display: "inline-flex", marginTop: "18px", padding: "8px 12px", borderRadius: "999px", background: "rgba(255,255,255,.14)", fontSize: "12px", fontWeight: 800 };
const card = { maxWidth: "920px", margin: "16px auto 0", background: "#fff", border: "1px solid #e2e8f0", borderRadius: "26px", padding: "22px", boxShadow: "0 14px 34px rgba(15,23,42,.08)" };
const section = { padding: "16px 0", borderBottom: "1px solid #edf2f7" };
const sectionTitle = { margin: 0, fontSize: "18px", fontWeight: 900, color: "#0f172a" };
const sectionText = { margin: "8px 0 0", color: "#475569", fontSize: "14px", lineHeight: 1.75 };

const ABOUT_SEO_SECTIONS = [
  { heading: `Our Mission`, body: `Our mission is to provide accurate, balanced, and easy-to-understand information about gambling-related topics, including the history of traditional games, probability, legal considerations, and responsible gambling practices. We aim to help readers better understand these subjects through factual and educational content.` },
  { heading: `Our Vision`, body: `Our vision is to become a trusted educational resource for readers seeking reliable information about gambling-related topics. We aim to simplify complex concepts such as probability, randomness, responsible gambling, and legal considerations through well-researched, easy-to-understand content. By providing balanced information, we hope to promote informed decision-making and greater public awareness.` },
  { heading: `What You'll Find on Our Website`, body: `Our website covers a wide range of educational topics, including:

Historical background of traditional gambling games

Card game and number-based game terminology

Probability and randomness explained

Responsible gambling guidance

Gambling laws and regulations in different jurisdictions

Common myths and misconceptions

Online safety and consumer awareness

Every article is designed to present information in a clear, neutral, and easy-to-read format.` },
  { heading: `Our Editorial Approach`, body: `We believe educational content should be accurate, transparent, and regularly reviewed. Our articles are created using publicly available information and are written to help readers understand gambling-related concepts without promoting participation.

Whenever laws or regulations change, we aim to update relevant content so readers have access to current information.` },
  { heading: `Why Trust Our Content?`, body: `We focus on providing:

Clear explanations written in simple language.

Fact-based educational content.

Neutral discussions of gambling-related topics.

Information that encourages informed decision-making.

Responsible gambling awareness throughout our content.` },
  { heading: `Responsible Gambling Awareness`, body: `We recognize that gambling can involve financial, emotional, and social risks. Throughout our website, we encourage readers to understand these risks before participating in any gambling activity.

Responsible gambling includes:

Understanding the risks involved.

Setting personal spending and time limits.

Avoiding gambling as a source of income.

Seeking help if gambling becomes difficult to control.` },
  { heading: `Educational Resources`, body: `Our educational articles explain topics such as:

The history of traditional gambling games.

Basic probability concepts.

Independent random events.

Common gambling misconceptions.

Financial responsibility.

Consumer protection and online safety.

These resources are intended to improve understanding rather than encourage gambling participation.` },
  { heading: `Online Safety`, body: `When researching or using online services, it is important to protect your personal information.

Good online safety practices include:

Using strong and unique passwords.

Enabling two-factor authentication where available.

Verifying that websites use secure HTTPS connections.

Reading privacy policies before sharing personal information.

Being cautious of phishing attempts and fraudulent websites.` },
  { heading: `Our Commitment to Accuracy`, body: `We strive to ensure our content is accurate at the time of publication. However, gambling regulations, licensing requirements, and industry practices may change over time. Readers are encouraged to consult official government or regulatory sources for the latest legal information relevant to their location.

Who Our Content Is For

Our content is designed for a broad audience, including:

Individuals interested in learning about the history of traditional gambling games.

Readers researching probability and random outcomes.

Students exploring gambling-related topics from an educational perspective.

People looking for information about responsible gambling practices.

Anyone interested in understanding gambling laws and regulations in different jurisdictions.

Our articles are written for informational purposes and are intended to be accessible to readers of all experience levels.

How We Create Our Content

Every article published on our website is prepared with an emphasis on clarity, accuracy, and neutrality. We review publicly available information, educational resources, and regulatory guidance where appropriate to ensure our content is informative and balanced.

Our editorial process focuses on:

Clear and understandable language.

Accurate presentation of historical information.

Neutral explanations of gambling-related concepts.

Regular updates when important information changes.

Understanding Randomness

Randomness is one of the most important concepts in games of chance. Random events occur without predictable patterns, meaning each independent outcome has its own probability regardless of previous results.

Understanding randomness helps readers recognize why no historical pattern, prediction method, or betting system can reliably determine future outcomes in independent games of chance.

Why Responsible Gambling Matters

Responsible gambling is about making informed choices and recognizing personal limits. Understanding both the entertainment value and the potential risks associated with gambling can help reduce financial and emotional harm.

Responsible gambling encourages individuals to:

Stay within predetermined spending limits.

Manage their time responsibly.

Recognize warning signs early.

Seek support if gambling begins affecting daily life.

Consumer Awareness

Being an informed consumer is important when researching gambling-related services. Individuals should understand licensing requirements, age restrictions, privacy policies, and applicable laws before using any gambling platform.

Learning how to evaluate online information critically can help readers distinguish factual educational content from unsupported claims.

Frequently Updated Information

Because gambling laws, regulations, and industry practices may change over time, we periodically review and update our educational content where appropriate.

This helps ensure readers have access to current information while maintaining a consistent focus on accuracy and responsible gambling awareness.

Transparency

We believe readers deserve content that is straightforward and unbiased. Our goal is to explain gambling-related topics clearly without making unrealistic claims or presenting gambling as a guaranteed way to achieve financial success.

Transparency helps readers better understand both the opportunities and the risks associated with gambling-related activities.

Community Education

Public awareness plays an important role in reducing gambling-related harm. Educational resources help individuals understand concepts such as probability, financial responsibility, legal compliance, and consumer protection.

By encouraging informed decision-making, educational content can contribute to healthier attitudes toward gambling-related activities.

Looking Ahead

As gambling technologies, regulations, and consumer expectations continue to evolve, we remain committed to expanding our educational resources. Our objective is to continue publishing clear, accurate, and responsible content that helps readers better understand gambling-related topics while emphasizing safety, legality, and informed decision-making.` },
  { heading: `Disclaimer`, body: `The information provided on this website is intended for general educational and informational purposes only. It should not be interpreted as legal, financial, or professional advice, nor as encouragement to participate in gambling activities. Readers are responsible for understanding and complying with the laws that apply in their own jurisdiction.

### 1. What is the purpose of this website?

This website is designed to provide educational and informational content about gambling-related topics, including the history of traditional games, probability, legal considerations, and responsible gambling practices.

### 2. Does this website provide gambling advice?

No. The information on this website is intended for educational purposes only and should not be considered gambling, financial, or legal advice.

### 3. Why do you publish educational content about gambling?

Our goal is to help readers understand gambling-related concepts, terminology, probability, and responsible gambling through factual and easy-to-understand articles.

### 4. Is the information on this website regularly updated?

Yes. We review and update our content whenever necessary to improve accuracy and reflect changes in laws, regulations, or educational information.

### 5. What topics are covered on this website?

We publish articles about gambling history, game terminology, probability, randomness, legal considerations, responsible gambling, consumer awareness, and online safety.

### 6. What is responsible gambling?

Responsible gambling means understanding the risks involved, setting personal limits on time and spending, and treating gambling as entertainment rather than a source of income.

### 7. Why is understanding probability important?

Probability helps explain how random events work and why past outcomes cannot reliably predict future results in independent games of chance.

### 8. Can historical results predict future outcomes?

No. Historical results are records of past events and should not be interpreted as a reliable method for predicting future outcomes.

### 9. Why do gambling laws differ between regions?

Each country, state, or territory establishes its own legal framework for gambling. As a result, regulations and licensing requirements vary from one jurisdiction to another.

### 10. Is online gambling legal everywhere?

No. The legality of online gambling depends entirely on the laws of your country, state, or region.

### 11. Does gambling guarantee profits?

No. Gambling involves financial risk, and there is no guaranteed strategy or system that can consistently produce profits.

### 12. What are the risks associated with gambling?

Potential risks include financial loss, emotional stress, debt, gambling-related harm, relationship difficulties, and reduced work or academic performance.

### 13. How can someone gamble more responsibly?

Responsible gambling includes setting spending limits, limiting play time, avoiding chasing losses, taking breaks, and seeking help if gambling becomes difficult to control.

### 14. What is the gambler's fallacy?

The gambler's fallacy is the mistaken belief that previous random outcomes influence future independent events. In reality, each independent event has its own probability.

### 15. Who should read the content on this website?

Our content is intended for anyone interested in learning about gambling-related topics, including students, researchers, general readers, and individuals seeking information about responsible gambling.

### 16. How do you ensure content accuracy?

We prepare our articles using publicly available educational resources and review them periodically to improve clarity and accuracy whenever appropriate.

### 17. Why is financial responsibility important?

Financial responsibility helps individuals avoid unnecessary losses by setting budgets, avoiding borrowed money, and separating gambling expenses from essential household costs.

### 18. What should someone do if gambling becomes a problem?

If gambling begins affecting finances, relationships, work, education, or mental well-being, individuals should consider contacting qualified healthcare professionals or local gambling support organizations.

### 19. Does this website encourage gambling?

No. Our objective is to provide balanced educational information, increase awareness of the risks, and promote informed decision-making and responsible gambling practices.

### 20. How can I contact your team?

If you have questions, suggestions, or feedback about our educational content, you can reach out through the contact information provided on our website. We welcome constructive feedback that helps improve the quality and accuracy of our resources.` },
];

const ABOUT_SEO_FAQS = [
  { q: "What is the purpose of this website?", a: "This website is designed to provide educational and informational content about gambling-related topics, including the history of Matka and Teen Patti, probability, responsible gambling, and legal considerations." },
  { q: "Does this website provide gambling advice?", a: "No. The information on this website is intended for educational purposes only and should not be considered gambling, financial, or legal advice." },
  { q: "Why do you publish educational content about gambling?", a: "Our goal is to help readers understand gambling-related concepts, terminology, probability, and responsible gambling through clear and balanced content." },
  { q: "Is the information on this website regularly updated?", a: "Yes. We review and update our content whenever necessary to improve accuracy and reflect changes in laws, regulations, or industry practices." },
  { q: "What is responsible gambling?", a: "Responsible gambling means understanding the risks involved, setting personal limits on time and spending, and treating gambling as entertainment rather than an investment or source of income." },
  { q: "Can historical results predict future outcomes?", a: "No. Historical results are records of past events and should not be interpreted as a reliable method for predicting future results in games of chance." },
  { q: "Why do gambling laws differ between regions?", a: "Each country, state, or territory establishes its own legal framework for gambling. As a result, regulations and licensing requirements vary significantly between different jurisdictions." },
  { q: "Does gambling guarantee profits?", a: "No. Gambling involves financial risk, and there is no guaranteed strategy or system that can consistently produce profits. Gambling should never be considered a source of income." },
  { q: "What are the risks associated with gambling?", a: "Potential risks include financial loss, emotional stress, debt, gambling-related harm, relationship difficulties, and reduced productivity at work or school." },
  { q: "What should someone do if gambling becomes a problem?", a: "If gambling begins affecting finances, relationships, work, education, or mental well-being, individuals should consider seeking help from a qualified counselor or a national gambling support organization." },
  { q: "Does this website encourage gambling?", a: "No. Our objective is to provide balanced educational information, increase awareness of the risks, and promote informed and responsible decision-making." },
  { q: "How can I contact your team?", a: "If you have questions, suggestions, or feedback about our educational content, you can reach out through the contact information provided on our website." },
];

export default LegalPage;
